import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Printer } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import CrudSection from "@/components/CrudSection";
import { PageHeader, fmtDate } from "@/components/common";
import SaleStatusCell from "@/components/SaleStatusCell";
import NoteCell from "@/components/NoteCell";
import AttachmentsCell from "@/components/AttachmentsCell";
import { listResource } from "@/lib/api";
import { printRecords } from "@/lib/exportPrint";

const SALESPEOPLE = ["Bryan", "Boone", "Graves", "Jeff", "Matt"];
const STATUSES = ["pickup_scheduled", "delivery_scheduled", "picked_up", "delivered"];
const today = new Date().toISOString().slice(0, 10);

const monthKey = (r) => (r.date_sold || "").slice(0, 7); // YYYY-MM
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const monthLabel = (key) => {
  if (!/^\d{4}-\d{2}$/.test(key)) return key;
  const [y, m] = key.split("-");
  return `${MONTH_NAMES[Number(m) - 1]} ${y}`;
};

const fields = [
  { key: "date_sold", label: "Date Sold", type: "date", default: today, required: true },
  { key: "unit", label: "Unit / Equipment", type: "text", full: true, required: true, placeholder: "JD 6120M Tractor" },
  { key: "customer", label: "Customer", type: "text", full: true, placeholder: "Buyer name" },
  { key: "stock_number", label: "Stock #", type: "text", required: true, placeholder: "N-4412" },
  { key: "salesperson", label: "Salesman", type: "select", default: "Bryan", required: true, options: SALESPEOPLE },
  { key: "price", label: "Sale Price", type: "text", placeholder: "$45,000" },
  { key: "trade_in_na", label: "No Trade-In", type: "checkbox", checkboxLabel: "N/A — no trade-in on this sale" },
  { key: "trade_in", label: "Trade-In (if any) — new unit to add", type: "text", full: true, placeholder: "Unit taken on trade" },
  { key: "status", label: "Status", type: "select", default: "pickup_scheduled", required: true, options: STATUSES, inputClass: "max-w-[220px]" },
  { key: "scheduled_date", label: "Pickup / Delivery Date", type: "date" },
  { key: "north_shop", label: "North Shop", type: "text", full: true, placeholder: "add unit to north shop section" },
  { key: "initials", label: "Your Initials", type: "text", required: true, maxLength: 2, placeholder: "e.g. BW" },
  { key: "notes", label: "Notes", type: "textarea", full: true },
];

const columns = [
  { key: "date_sold", label: "Sold", mono: true, render: (r) => r.date_sold ? fmtDate(r.date_sold) : <span className="text-gray-300">—</span> },
  { key: "unit", label: "Unit / Equipment", render: (r) => (
      <div>
        <div className="font-medium">{r.unit || "—"}</div>
        {r.stock_number && <div className="text-xs text-gray-500 font-mono-data mt-0.5">{r.stock_number}</div>}
      </div>
    ) },
  { key: "salesperson", label: "Salesman", render: (r) => <span className="font-medium">{r.salesperson || "—"}</span> },
  { key: "customer", label: "Customer", render: (r) => r.customer || <span className="text-gray-300">—</span> },
  { key: "price", label: "Price", align: "right", render: (r) => r.price || <span className="text-gray-300">—</span> },
  { key: "trade_in", label: "Trade-In", render: (r) => r.trade_in_na ? <span className="text-xs font-bold uppercase tracking-wide text-gray-500">N/A</span> : (r.trade_in || <span className="text-gray-300">—</span>) },
  { key: "status", label: "Status", render: (r) => <SaleStatusCell row={r} /> },
  { key: "north_shop", label: "North Shop", align: "center", render: (r) => r.north_shop ? (
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex" data-testid="sale-ns-flag"><Check className="w-5 h-5 text-green-600 mx-auto" /></span>
          </TooltipTrigger>
          <TooltipContent className="rounded-none border-2 border-gray-900 bg-white text-gray-900 max-w-xs whitespace-normal">
            <span className="font-bold uppercase tracking-wide text-[10px] block text-gray-500">North Shop</span>
            {r.north_shop}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : <span className="text-gray-300">—</span> },
  { key: "notes", label: "Notes", render: (r) => <NoteCell row={r} path="sales" /> },
  { key: "attachments", label: "Files", render: (r) => <AttachmentsCell row={r} path="sales" /> },
];

const exportColumns = [
  { label: "Sold", value: (r) => (r.date_sold ? fmtDate(r.date_sold) : "") },
  { label: "Unit", value: (r) => r.unit },
  { label: "Customer", value: (r) => r.customer },
  { label: "Stock #", value: (r) => r.stock_number },
  { label: "Salesman", value: (r) => r.salesperson },
  { label: "Price", value: (r) => r.price },
  { label: "Trade-In", value: (r) => (r.trade_in_na ? "N/A" : r.trade_in) },
  { label: "Status", value: (r) => (r.status || "").replace(/_/g, " ") },
  { label: "Sched", value: (r) => (r.scheduled_date ? fmtDate(r.scheduled_date) : "") },
  { label: "Website", value: (r) => (r.off_website ? "Off website" : "On website") },
  { label: "North Shop", value: (r) => r.north_shop },
  { label: "Notes", value: (r) => r.notes },
];

const ExportBar = ({ label, records }) => (
  <div className="flex justify-end mb-4">
    <Button data-testid="sales-export-btn" variant="outline"
      onClick={() => printRecords({ title: `Recently Sold — ${label}`, path: "sales", records, columns: exportColumns })}
      className="rounded-none border-2 border-gray-900 font-bold uppercase tracking-wide hover:bg-gray-900 hover:text-white">
      <Printer className="w-4 h-4 mr-2" /> Print / Export
    </Button>
  </div>
);

export default function Sales() {
  const { data: sales = [] } = useQuery({ queryKey: ["sales"], queryFn: () => listResource("sales"), refetchInterval: 5000 });
  const currentKey = new Date().toISOString().slice(0, 7);

  // Past months (before current) that have sales — newest month first.
  const pastMonths = [...new Set(sales.map(monthKey).filter((k) => /^\d{4}-\d{2}$/.test(k) && k < currentKey))].sort().reverse();

  const commonProps = {
    path: "sales",
    fields,
    columns,
    editLabel: "Edit Sold Unit",
    noDelete: true,
  };

  return (
    <div data-testid="sales-section">
      <PageHeader title="Recently Sold" subtitle="Recently sold units — current month on the main tab, past months archived below" />
      <Tabs defaultValue="current" className="w-full">
        <TabsList className="mb-6 flex flex-wrap h-auto justify-start gap-2 bg-transparent p-0">
          <TabsTrigger value="current" data-testid="sales-tab-current"
            className="rounded-none border-2 border-gray-900 px-4 py-2 font-bold uppercase tracking-wide text-xs data-[state=active]:bg-gray-900 data-[state=active]:text-white">
            Current Month
          </TabsTrigger>
          {pastMonths.map((k) => (
            <TabsTrigger key={k} value={k} data-testid={`sales-tab-${k}`}
              className="rounded-none border-2 border-gray-900 px-4 py-2 font-bold uppercase tracking-wide text-xs data-[state=active]:bg-gray-900 data-[state=active]:text-white">
              {monthLabel(k)}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="current" className="mt-0">
          <ExportBar label="Current Month" records={sales.filter((r) => monthKey(r) >= currentKey || !monthKey(r))} />
          <CrudSection {...commonProps} title="Current Month" subtitle="" testPrefix="sales" addLabel="Sold Unit" searchable
            filter={(r) => monthKey(r) >= currentKey || !monthKey(r)} />
        </TabsContent>
        {pastMonths.map((k) => (
          <TabsContent key={k} value={k} className="mt-0">
            <ExportBar label={monthLabel(k)} records={sales.filter((r) => monthKey(r) === k)} />
            <CrudSection {...commonProps} title={monthLabel(k)} subtitle="archived" testPrefix={`sales-${k}`} hideAdd searchable
              filter={(r) => monthKey(r) === k} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
